import { createContext } from 'react';
import { ResourceId } from 'src/api/deinverband-api';
import { Id } from 'src/types/common';
import { ItemStore, PatchApi } from 'src/types/hooks';
import { CompanyContextType } from './company-context';
import { DraftContextType } from './draft-context';
import { EmailTemplateContextType } from './email-template-context';
import { EntryContextType } from './entry-context';
import { GroupContextType } from './group-context';
import { ListContextType } from './list-context';
import { MyProfileContextType } from './my-profile-context';
import { OccasionContextType } from './occasion-context';
import { PostContextType } from './post-context';
import { ProfileContextType } from './profile-context';
import { SearchEntryContextType } from './search-entry-context';
import { SurveyContextType } from './survey-context';

/**
 * Provides re-usable item state management
 *
 * This context spans around a single item (like a post or a company) and
 * provides additional actions depending on the item type and use case. This
 * avoids passing down a lot of properties and handlers. Currently a single
 * context type is used, which can be accessed via standardized hooks.
 *
 * However, that means that there can not be more than one item context.
 */
export interface ItemStoreContextType<
  Type,
  Store extends ItemStore<{ id: Id }, { id?: ResourceId }>,
  Api,
  Extra = undefined
> {
  // unique type identifier for the item store context
  type: Type;
  // item store for data and loading state
  store: Store;
  // access to patch and underlying API
  api: Api;
  // additional item-specific data and actions
  extra: Extra;
}

export type ItemStoreContextBase = ItemStoreContextType<
  string,
  ItemStore<{ id: Id }, { id?: ResourceId }>,
  unknown,
  unknown
>;

export const ItemStoreContext = createContext<ItemStoreContextBase | undefined>(
  undefined
);

// item store context type registry
export type ItemStoreContextTypes = {
  company: CompanyContextType;
  emailTemplate: EmailTemplateContextType;
  entry: EntryContextType;
  group: GroupContextType;
  list: ListContextType;
  occasion: OccasionContextType;
  post: PostContextType | DraftContextType;
  profile: ProfileContextType | MyProfileContextType;
  searchEntry: SearchEntryContextType;
  survey: SurveyContextType;
};

// default properties for a provider
export type PropsWithItemStore<
  T extends object,
  Type extends keyof ItemStoreContextTypes & string,
  Api = PatchApi<InferRemote<Type>, InferPatch<Type>>
> = T & {
  store: InferItemStore<Type>;
  // minimum required API constraints
  api?: Api;
};

export type InferApi<T extends keyof ItemStoreContextTypes & string> =
  ItemStoreContextTypes[T]['api'];

export type InferItemStore<T extends keyof ItemStoreContextTypes & string> =
  ItemStoreContextTypes[T]['store'];

export type InferLocal<T extends keyof ItemStoreContextTypes & string> =
  NonNullable<ItemStoreContextTypes[T]['store']['item']>;

export type InferRemote<T extends keyof ItemStoreContextTypes & string> =
  NonNullable<ItemStoreContextTypes[T]['store']['remote']>;

export type InferPatch<T extends keyof ItemStoreContextTypes & string> =
  ItemStoreContextTypes[T]['api'] extends PatchApi<object, infer Patch>
    ? Patch
    : never;
